/**
 * LinkCard Component
 * Displays a single link as a card with OG metadata
 */

import { useState } from "react";
import { Effect } from "effect";
import { toast } from "sonner";
import {
  ExternalLink,
  RotateCw,
  Pencil,
  Trash2,
  Check,
  AlertCircle,
  Clock,
} from "lucide-react";
import type { Link } from "../types/link";
import { deleteLink, refetchOgMetadata } from "../services/linkService";
import {
  Card,
  CardContent,
  CardFooter,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { DeleteLinkDialog } from "./DeleteLinkDialog";
import { EditLinkForm } from "./EditLinkForm";

interface LinkCardProps {
  link: Link;
  onDelete?: () => void;
  onEdit?: () => void;
}

export function LinkCard({ link, onDelete, onEdit }: LinkCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);
    setIsDeleting(true);

    const program = deleteLink(link.id).pipe(
      Effect.tap(() =>
        Effect.sync(() => {
          toast.success("링크가 삭제되었습니다");
          onDelete?.();
        })
      ),
      Effect.tapError((error) =>
        Effect.sync(() => {
          const message =
            error._tag === "NotFoundError"
              ? "링크를 찾을 수 없습니다"
              : "삭제 중 오류가 발생했습니다";
          toast.error("삭제 실패", { description: message });
        })
      ),
      Effect.catchAll(() => Effect.void)
    );

    try {
      await Effect.runPromise(program);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefetchOg = async () => {
    setIsRefetching(true);

    const program = refetchOgMetadata(link.id).pipe(
      Effect.tap(() =>
        Effect.sync(() => {
          toast.success("메타데이터가 업데이트되었습니다");
          onEdit?.();
        })
      ),
      Effect.tapError((error) =>
        Effect.sync(() => {
          const message =
            error._tag === "NotFoundError"
              ? "링크를 찾을 수 없습니다"
              : "메타데이터 가져오기 실패";
          toast.error("업데이트 실패", { description: message });
        })
      ),
      Effect.catchAll(() => Effect.void)
    );

    try {
      await Effect.runPromise(program);
    } finally {
      setIsRefetching(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    onEdit?.();
  };

  // Show edit form when editing
  if (isEditing) {
    return (
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          <EditLinkForm
            link={link}
            onSuccess={handleEditSuccess}
            onCancel={() => setIsEditing(false)}
          />
        </CardContent>
      </Card>
    );
  }

  const displayTitle = link.title || link.ogMetadata?.title || link.url;
  const displayDescription = link.ogMetadata?.description;
  const displayImage = link.ogMetadata?.image;
  const ogStatus = link.ogMetadata?.status || "pending";

  const getStatusBadge = () => {
    switch (ogStatus) {
      case "pending":
        return (
          <Badge variant="secondary" className="gap-1">
            <Clock className="h-3 w-3" />
            로딩중
          </Badge>
        );
      case "success":
        return (
          <Badge variant="success" className="gap-1">
            <Check className="h-3 w-3" />
            완료
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="warning" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            실패
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card className="group overflow-hidden transition-shadow hover:shadow-lg">
        {/* OG Image */}
        {displayImage && (
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block aspect-video cursor-pointer overflow-hidden bg-muted"
          >
            <img
              src={displayImage}
              alt={displayTitle}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </a>
        )}

        <CardContent className="p-4">
          {/* Title */}
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group/link flex cursor-pointer items-start gap-1 font-semibold hover:underline"
          >
            <span className="line-clamp-2">{displayTitle}</span>
            <ExternalLink className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover/link:opacity-100" />
          </a>

          {/* URL */}
          {(link.title || link.ogMetadata?.title) && (
            <p className="mt-1 truncate text-sm text-muted-foreground">
              {new URL(link.url).hostname}
            </p>
          )}

          {/* Description */}
          {displayDescription && (
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
              {displayDescription}
            </p>
          )}

          {/* Tags */}
          {link.tags && link.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {link.tags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="cursor-pointer text-xs hover:bg-accent"
                >
                  {tag.name}
                </Badge>
              ))}
              {link.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{link.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            {getStatusBadge()}
            <span>{formatDate(link.createdAt)}</span>
          </div>
        </CardContent>

        <CardFooter className="border-t p-2">
          <div className="flex w-full justify-end gap-1">
            {(ogStatus === "failed" || ogStatus === "success") && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefetchOg}
                disabled={isRefetching || isDeleting}
                aria-label="메타데이터 새로고침"
                className="cursor-pointer"
              >
                <RotateCw
                  className={`h-4 w-4 ${isRefetching ? "animate-spin" : ""}`}
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsEditing(true)}
              disabled={isDeleting || isRefetching}
              aria-label="수정"
              className="cursor-pointer"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDeleting || isRefetching}
              aria-label="삭제"
              className="cursor-pointer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardFooter>
      </Card>

      <DeleteLinkDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        linkTitle={displayTitle}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </>
  );
}
